import useCreateSale from '@/hooks/queries/sale/useCreateSale';
import useGetUser from '@/hooks/queries/user/useGetUser';
import { useRootStore } from '@/providers/root-store-provider';
import formBookingDatepickerSchema from '@/schemas/formBookingDatepicker';
import formParticipantDataSchema from '@/schemas/formParticipantData';
import { TOrganizationFAQ } from '@/services/api/organization-api';
import { Service } from '@/services/api/services-api';
import { StepService } from '@/stores/slices/step-service/step-services.types';
import { getTimestampUTC } from '@/utils/date';
import { useUser } from '@auth0/nextjs-auth0/client';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { z } from 'zod';

type TFormLandingRental = {
  service: Service;
  organization: TOrganizationFAQ;
};

export const useFormLandingRental = ({
  service,
  organization
}: TFormLandingRental) => {
  const [isOpenSummaryModal, setIsOpenSummaryModal] = useState(false);
  const [isSaleCreated, setIsSaleCreated] = useState(false);
  const stepServicesData: StepService | null = useRootStore(
    (store) => store.stepServicesData
  );
  const loadStepServicesData = useRootStore(
    (store) => store.loadStepServicesData
  );
  const cleanStepServicesData = useRootStore(
    (store) => store.cleanStepServicesData
  );
  const isDataExpired = useRootStore((store) => store.isDataExpired);
  const purchaseCompleted = useRootStore((store) => store.purchaseCompleted);
  const setPurchaseCompleted = useRootStore(
    (store) => store.setPurchaseCompleted
  );
  const { mutate, isPending: isPendingSale } = useCreateSale();

  const { user: userAuth0 } = useUser();
  const { data: userData, isPending: isPendingUserData } = useGetUser(
    userAuth0?.email
  );

  const bookingDateSchema = formBookingDatepickerSchema;
  const participantSchema = formParticipantDataSchema({
    minAge: service?.minAge,
    maxAge: service?.maxAge
  });

  const schema = bookingDateSchema.merge(participantSchema._def.schema);

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      date: undefined,
      time: undefined,
      participants: [],
      user: {
        email: userData?.email ?? stepServicesData?.email,
        firstName: userData?.firstName ? userData?.firstName : '',
        lastName: userData?.lastName ? userData?.lastName : '',
        birthdate: userData?.birthdate
          ? new Date(userData?.birthdate)
          : undefined,
        height: userData?.height ? userData?.height : 0,
        weight: userData?.weight ? userData?.weight : 0,
        phone: userData?.phone ? userData?.phone : '',
        identification: {
          number: userData?.identification?.number
            ? userData?.identification?.number
            : '',
          type: userData?.identification?.type
            ? userData?.identification?.type
            : ''
        },
        userIsParticipant: true
      },
      acceptedTos: false,
      declarationLegalAge: false
    }
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'participants'
  });

  const onSubmit = () => {
    if (service) {
      const formValues = form.getValues();

      const userParticipantData = {
        ...formValues.user,
        birthdate: formValues.user.birthdate
          ? formValues.user.birthdate.toISOString()
          : '', // parte date to ISO string
        isOwnUser: true // add value to indicate de user is participant
      };

      delete (userParticipantData as { userIsParticipant?: boolean })
        .userIsParticipant; // the end-point does not support this value

      const participants = formValues.participants.map((p) => {
        const { id, ...rest } = p;
        if (id) return id;
        return {
          ...rest,
          birthdate: p.birthdate.toISOString()
        };
      });

      const consumers = formValues.user.userIsParticipant
        ? [userParticipantData, ...participants]
        : participants;

      const { date, time } = form.getValues();
      const firstReservationStartTime = getTimestampUTC({
        date,
        time: time.from,
        tz: organization.tz
      });

      const parsedValues = {
        acceptedTos: formValues.acceptedTos,
        categorySport: service.sport.id,
        consumers,
        email: formValues.user.email,
        firstName: formValues.user.firstName,
        lastName: formValues.user.lastName,
        paymentType: 'ef75aed8-40fb-489f-963c-11722aa12440', // TODO: add payment type on service?
        phone: formValues.user.phone,
        service: service.id,
        firstReservationStartTime
      };
      mutate(parsedValues);
      setIsOpenSummaryModal(false);
      setIsSaleCreated(true);
      cleanStepServicesData();
    }
  };

  useEffect(() => {
    const isExpired = isDataExpired();
    if (isExpired) {
      cleanStepServicesData();
    }
    if (purchaseCompleted) {
      setPurchaseCompleted(false);
      cleanStepServicesData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (service) {
      form.reset({
        ...form.getValues(),
        // Participants array, based on user is a participant too
        participants: Array.from({ length: service.minConsumers - 1 }, () => ({
          firstName: '',
          lastName: '',
          birthdate: undefined,
          height: 0,
          weight: 0,
          id: ''
        }))
      });
    }
    loadStepServicesData({
      service,
      firstReservationStartTime: undefined
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service]);

  return {
    form,
    fields,
    addParticipant: append,
    removeParticipant: remove,
    userAuth0,
    userData,
    service,
    isPendingSale,
    isOpenSummaryModal,
    isSaleCreated,
    mutate,
    onSubmit,
    setIsOpenSummaryModal,
    setIsSaleCreated
  };
};
